import { createFileRoute, redirect } from "@tanstack/react-router";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import type { FeatureSet } from "common/constants/features";
import Showroom from "lib/components/builders/communities/plans/showroom/Showroom";
import LoadingPage from "lib/components/layout/LoadingPage";
import { useFetchImmutable } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import getPageTitle from "lib/utils/getPageTitle";
import { createFakePlanData } from "lib/utils/showroom/showroomData";
import { useMemo } from "react";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../components/withAuthentication";

function StudioShowroom() {
  const { isReady, builderId, id: studioDocId } = useAppRouterParams();

  const { enabledFeatures } = useUserContext();

  if (!isReady || !studioDocId || !enabledFeatures) {
    return <LoadingPage />;
  }

  return <ReadyStudioShowroom builderId={builderId} studioDocId={studioDocId} enabledFeatures={enabledFeatures} />;
}

function ReadyStudioShowroom({
  builderId,
  studioDocId,
  enabledFeatures,
}: {
  builderId: string | undefined;
  studioDocId: string;
  enabledFeatures: FeatureSet;
}) {
  const builderIdQuery = builderId ? `?builderId=${builderId}` : "";
  const { data: studioDoc } = useFetchImmutable<ClientStudioDocHydrated>(
    `${WEB_API_BASE_PATH}/studio-docs/${studioDocId}${builderIdQuery}`,
    [studioDocId],
  );

  const planData: ClientHomePlanBaseData | null = useMemo(
    () => (studioDoc ? createFakePlanData(studioDoc, enabledFeatures) : null),
    [studioDoc, enabledFeatures],
  );

  if (!planData) {
    return <LoadingPage />;
  }

  return (
    <>
      <Head>
        <title>{getPageTitle(`Configure ${planData.homePlan.planSetId}`)}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <Showroom
        builder={planData.builder}
        location={planData.community}
        lot={planData.lot}
        masterPlan={planData.masterPlan}
        locationPlan={planData.communityPlan}
        homePlan={planData.homePlan}
        lotDiscounts={planData.lotDiscounts}
        homePlanDiscounts={planData.homePlanDiscounts}
        previewMode
      />
    </>
  );
}

export const Route = createFileRoute("/studio-showroom")({
  beforeLoad: ({ context, search }) => {
    if (!context.enabledFeatures.showroomUnification) {
      return;
    }
    const { id, builderId } = search as { id?: string; builderId?: string };
    if (!id) {
      return;
    }
    throw redirect({
      to: "/showroom/studio",
      search: { studioDocId: id, ...(builderId ? { builderId } : {}) },
      replace: true,
    });
  },
  component: withAuthentication(StudioShowroom, {
    products: ["showroom"],
  }),
});
